import {useMemo} from 'react'
import {useOtaMode} from 'hooks/useOtaMode'
import {SearchOffer} from 'modules/sapiSearch/slice'

import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {VioTagDetails, VioTagSavingType} from '@vio/core/src/tag/types'
import {
  applyRoundingStrategy,
  RoundingStrategyVariants,
  toDecreasePercentage
} from '@vio/core/src/utils/number'

const SAVING_THRESHOLD = 5
const ALLOWED_CURRENCIES_TO_SHOW_PRICE = [
  'EUR',
  'USD',
  'GBP',
  'AUD',
  'CHF',
  'KWD',
  'SGD',
  'NZD',
  'BHD',
  'CAD'
]

interface Props {
  price?: number
  anchorPrice?: number
  currencyCode?: string
  offerTags?: SearchOffer['tags']
}

/**
 * A hook to calculate the Vio tag details.
 * @param param - The parameters object
 * @param param.price - The price of the offer.
 * @param param.anchorPrice - The anchor price of the offer.
 * @param param.currencyCode - The currency code of the offer.
 * @param param.offer - Search Offer
 * @returns The Vio tag details.
 */
export const useVioTag = ({
  price = 0,
  anchorPrice = 0,
  currencyCode = '',
  offerTags = []
}: Props): VioTagDetails => {
  const {
    monetisation: {onlyOnBrandTag}
  } = useBrandCapabilities()
  const roundedPrice =
    applyRoundingStrategy(RoundingStrategyVariants.round, price) ?? 0
  const roundedAnchorPrice =
    applyRoundingStrategy(RoundingStrategyVariants.round, anchorPrice) ?? 0
  const savingPercentage =
    toDecreasePercentage(
      roundedPrice,
      roundedAnchorPrice,
      RoundingStrategyVariants.floor
    ) ?? 0

  const {isOTAMode} = useOtaMode()

  const isVioTagVisible: boolean = useMemo(() => {
    if (isOTAMode) return false

    return onlyOnBrandTag.enabled && (offerTags || []).includes('exclusive')
  }, [isOTAMode, onlyOnBrandTag.enabled, offerTags])

  const savingType: VioTagSavingType = useMemo(() => {
    if (
      savingPercentage > SAVING_THRESHOLD &&
      ALLOWED_CURRENCIES_TO_SHOW_PRICE.includes(currencyCode)
    )
      return 'number'
    return 'percentage'
  }, [savingPercentage, currencyCode])

  return {
    savingPrice: roundedAnchorPrice - roundedPrice,
    savingPercentage,
    isVioTagVisible,
    savingType,
    tagMessage: onlyOnBrandTag.enabled
      ? onlyOnBrandTag.config.message
      : undefined
  }
}
